var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ResourceResolver from "../Util/ResourceResolver";
import Accessor from "../Accessor/Accessor";
import Matrix from "grimoirejs-math/ref/Matrix";
import Animation from "../Animation/Animation";
import MaterialParser from "./MaterialParser";
import GLTFConstantConverter from "./ConstantConverter";
import Vector3 from "grimoirejs-math/ref/Vector3";
import AABB from "grimoirejs-math/ref/AABB";
import Buffer from "grimoirejs-fundamental/ref/Resource/Buffer";
import Geometry from "grimoirejs-fundamental/ref/Geometry/Geometry";
import Texture2D from "grimoirejs-fundamental/ref/Resource/Texture2D";
export default class GLTFParser {
    static parseFromURL(gl, url) {
        return __awaiter(this, void 0, void 0, function* () {
            const resourceResolver = new ResourceResolver(url);
            const tf = yield resourceResolver.loadGLTFFile();
            const rawBuffer = {};
            const rawbufferView = {};
            const meshes = {};
            const buffers = {};
            const images = {};
            const textures = {};
            const animations = {};
            const skins = {};
            const materials = {};
            const accessors = {};
            // constructing buffers
            for (let key in tf.buffers) {
                if (key === "binary_glTF") {
                    rawBuffer[key] = resourceResolver.binaryGLTFBuffer;
                }
                else {
                    rawBuffer[key] = yield resourceResolver.loadBuffer(tf.buffers[key].uri);
                }
            }
            for (let key in tf.bufferViews) {
                const bufferView = tf.bufferViews[key];
                const currentBuffer = rawBuffer[bufferView.buffer];
                rawbufferView[key] = new Uint8Array(currentBuffer, bufferView.byteOffset, bufferView.byteLength);
                if (bufferView.target === void 0) {
                }
                else {
                    const buffer = buffers[key] = new Buffer(gl, bufferView.target, WebGLRenderingContext.STATIC_DRAW);
                    buffer.update(rawbufferView[key]);
                }
            }
            // constructing meshes
            for (let key in tf.meshes) {
                meshes[key] = GLTFParser._parseMesh(gl, tf, key, buffers, rawbufferView);
            }
            // constructing textures
            const imgLoadTask = [];
            for (let key in tf.images) {
                imgLoadTask.push(resourceResolver.loadImage(tf.images[key]).then(t => {
                    images[key] = t;
                }));
            }
            yield Promise.all(imgLoadTask);
            // parse textures
            for (let key in tf.textures) {
                const texInfo = tf.textures[key];
                const sampler = tf.samplers[texInfo.sampler];
                const tex = textures[key] = new Texture2D(gl);
                tex.magFilter = sampler.magFilter || WebGLRenderingContext.LINEAR;
                tex.minFilter = sampler.minFilter || WebGLRenderingContext.NEAREST_MIPMAP_LINEAR;
                tex.wrapS = sampler.wrapS || WebGLRenderingContext.REPEAT;
                tex.wrapT = sampler.wrapT || WebGLRenderingContext.REPEAT;
                tex.update(images[texInfo.source]);
            }
            for (let key in tf.materials) {
                const material = tf.materials[key];
                if (material.extensions !== void 0 && material.extensions.KHR_materials_common) {
                    materials[key] = yield MaterialParser.parse(tf, key, resourceResolver, textures);
                }
                else {
                    materials[key] = yield MaterialParser.parse(tf, key, resourceResolver, textures);
                }
            }
            // parse animations
            if (tf.animations) {
                for (let key in tf.animations) {
                    animations[key] = new Animation(tf, key, rawbufferView);
                }
            }
            if (tf.skins) {
                for (let key in tf.skins) {
                    const skin = tf.skins[key];
                    const accessor = tf.accessors[skin.inverseBindMatrices];
                    skins[key] = {
                        bindShapeMatrix: new Matrix(skin.bindShapeMatrix),
                        jointNames: skin.jointNames,
                        inverseBindMatrices: new Accessor(rawbufferView[accessor.bufferView], accessor.count, accessor.componentType, GLTFConstantConverter.asVectorSize(accessor.type), accessor.byteOffset || 0, accessor.byteStride || 0),
                        jointMatrices: new Float32Array(16 * skin.jointNames.length)
                    };
                }
            }
            return {
                meshes: meshes,
                textures: textures,
                tf: tf,
                materials: materials,
                animations: animations,
                skins: skins
            };
        });
    }
    static _parseMesh(gl, tf, meshName, buffers, arrayBuffers) {
        const meshInfo = tf.meshes[meshName];
        const geometries = [];
        for (let p = 0; p < meshInfo.primitives.length; p++) {
            const geometry = new Geometry(gl);
            const primitive = meshInfo.primitives[p];
            const topology = primitive.mode || WebGLRenderingContext.TRIANGLES;
            if (primitive.indices) {
                const indexAccessor = tf.accessors[primitive.indices];
                geometry.addIndex("default", buffers[indexAccessor.bufferView], topology, indexAccessor.byteOffset, indexAccessor.count, indexAccessor.componentType);
            }
            else {
                // should generate new index buffer for primitives
                const vertCount = tf.accessors[primitive.attributes["POSITION"]].count;
                const bufferInfo = GLTFConstantConverter.indexCountToBufferInfo(vertCount);
                const ibuf = new Buffer(gl, WebGLRenderingContext.ELEMENT_ARRAY_BUFFER, WebGLRenderingContext.STATIC_DRAW);
                const array = new bufferInfo.ctor(vertCount);
                for (var i = 0; i < vertCount; i++) {
                    array[i] = i;
                }
                ibuf.update(array);
                geometry.addIndex("default", ibuf, topology, 0, vertCount, bufferInfo.elementType);
            }
            // parse verticies
            const attribInfo = {};
            const usedBuffers = {};
            let aabb;
            if (!primitive.attributes["TEXCOORD_0"]) {
                const posAttr = tf.accessors[primitive.attributes["POSITION"]];
                const uvBuf = new Buffer(gl, WebGLRenderingContext.ARRAY_BUFFER, WebGLRenderingContext.STATIC_DRAW);
                uvBuf.update(new Float32Array(new ArrayBuffer(8 * posAttr.count)));
                usedBuffers["@@UV"] = uvBuf;
                geometry.addAttributes(uvBuf, {
                    TEXCOORD_0: {
                        size: 2
                    }
                });
            }
            for (let attrib in primitive.attributes) {
                const accessor = tf.accessors[primitive.attributes[attrib]];
                usedBuffers[accessor.bufferView] = buffers[accessor.bufferView];
                if (attrib === "POSITION") {
                    if (accessor.max && accessor.min) {
                        aabb = new AABB([new Vector3(accessor.max[0], accessor.max[1], accessor.max[2]), new Vector3(accessor.min[0], accessor.min[1], accessor.min[2])]);
                    }
                    else {
                        aabb = GLTFParser._genAABB(arrayBuffers[accessor.bufferView], accessor.byteStride, accessor.byteOffset, accessor.count);
                    }
                }
                const bufAccessor = {};
                bufAccessor[attrib] = {
                    size: GLTFConstantConverter.asVectorSize(accessor.type),
                    type: accessor.componentType,
                    stride: accessor.byteStride,
                    offset: accessor.byteOffset
                };
                geometry.addAttributes(buffers[accessor.bufferView], bufAccessor);
            }
            geometry["materialName"] = primitive.material; // TODO fix this bad implementation to find material from geometry
            geometries.push(geometry);
        }
        return geometries;
    }
    static _genAABB(view, stride, offset, count) {
        const aabb = new AABB();
        const dView = new Float32Array(view.buffer, view.byteOffset);
        for (var i = offset; i < offset + (count - 1) * stride; i += stride) {
            aabb.expand(new Vector3(dView[i], dView[i + 1], dView[i + 2]));
        }
        return aabb;
    }
}



// WEBPACK FOOTER //
// ./src/Parser/Parser.ts